/**
 * Shared pieces of the SEARCH build path. Two objectives sit on it — Leads
 * (/google-ads/leads) and Website traffic (/google-ads/traffic) — and nothing
 * else imports this file. Google has no objective on a campaign; the two
 * differ in bidding defaults, the lead-form step and wording. The CRM tags
 * each campaign with the objective it was built under so each workspace lists
 * its own (see `searchObjectiveOf`).
 *
 * Components are MODULE SCOPE on purpose: defining them inside a page
 * remounts them on every keystroke and loses focus.
 */
import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Check, Globe, Lock, Monitor, MousePointerClick, Plus, Shuffle, SlidersHorizontal, Smartphone, Sparkles, Target, TrendingUp,
  Wand2, X, type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import {
  GoogleAdsCampaignSummary, SearchAiDraftGroup, SearchBidding, SearchMatchType, SearchObjectiveKey,
} from '@/services/google-ads.service';
import { Label } from '@/components/google-ads/youtube/youtubeShared';
import { hostOf, isHttpUrl, MoneyInput, toMicros, uid } from '@/components/google-ads/objectives/objectiveShared';

// ── objective config ─────────────────────────────────────────────────────

export interface SearchObjectiveConfig {
  key: SearchObjectiveKey;
  title: string;
  base: string;
  subtitle: string;
  icon: LucideIcon;
  /** Leads can attach a Google lead form to the campaign. */
  leadForms: boolean;
  defaultBidding: SearchBidding;
  /** Top-strip "Goal:" line. */
  goal: string;
  /** List empty state + wizard intro. */
  emptyHint: string;
}

// ── bidding ──────────────────────────────────────────────────────────────

export interface BiddingOption {
  key: SearchBidding; title: string; desc: string; hint: string; icon: LucideIcon;
  /** Google refuses these until the account tracks conversions (CONVERSION_TRACKING_NOT_ENABLED). */
  needsConversions: boolean;
}

export const SEARCH_BIDDING_OPTIONS: ReadonlyArray<BiddingOption> = [
  { key: 'MAXIMIZE_CLICKS', title: 'Maximize clicks', icon: MousePointerClick, needsConversions: false,
    desc: 'As many visits as the budget allows — the safest start for a new account.', hint: 'Optional max CPC cap' },
  { key: 'MAXIMIZE_CONVERSIONS', title: 'Maximize conversions', icon: Target, needsConversions: true,
    desc: 'Google bids for the searches most likely to call, book or fill in a form.', hint: 'Optional target cost per lead' },
  { key: 'MAXIMIZE_CONVERSION_VALUE', title: 'Maximize conversion value', icon: TrendingUp, needsConversions: true,
    desc: 'Bids toward the most valuable conversions — needs conversion values.', hint: 'Optional target ROAS' },
  { key: 'MANUAL_CPC', title: 'Manual CPC', icon: SlidersHorizontal, needsConversions: false,
    desc: 'You set the highest price per click on each ad group.', hint: 'Full control' },
];

export const biddingTitle = (b: SearchBidding) => SEARCH_BIDDING_OPTIONS.find((o) => o.key === b)?.title ?? b;
export const needsConversions = (b: SearchBidding) => !!SEARCH_BIDDING_OPTIONS.find((o) => o.key === b)?.needsConversions;

/** Bidding picker. Conversion strategies lock when Google has told us the account has no conversion tracking. */
export function BiddingCards({ value, onChange, recommended, conversionBlocked, disabled }: {
  value: SearchBidding; onChange: (v: SearchBidding) => void; recommended: SearchBidding; conversionBlocked: boolean; disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      {SEARCH_BIDDING_OPTIONS.map((o) => {
        const locked = o.needsConversions && conversionBlocked;
        const on = value === o.key;
        const Icon = o.icon;
        return (
          <button key={o.key} type="button" disabled={disabled || locked} onClick={() => onChange(o.key)} aria-pressed={on}
            className={clsx(
              'relative flex gap-3 rounded-xl border p-3.5 text-left transition-colors',
              on ? 'border-primary bg-primary-soft/40' : 'border-border bg-surface hover:bg-surface-alt',
              (disabled || locked) && 'cursor-not-allowed opacity-60 hover:bg-surface',
            )}>
            <span className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', on ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-muted')}>
              {locked ? <Lock size={15} /> : <Icon size={16} />}
            </span>
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-text-primary">{o.title}</span>
                {o.key === recommended && !locked && <span className="rounded-full bg-primary-soft px-1.5 py-0.5 text-[10px] font-semibold text-primary">Recommended</span>}
              </span>
              <span className="mt-0.5 block text-2xs leading-4 text-text-muted">{o.desc}</span>
              <span className={clsx('mt-1 block text-2xs font-medium', locked ? 'text-warning' : 'text-text-secondary')}>
                {locked ? 'Needs conversion tracking on this account' : o.hint}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── campaign lists: which Search campaigns belong to which objective ─────

export const isSearchCampaign = (c: GoogleAdsCampaignSummary) => c.advertisingChannelType === 'SEARCH';
export const isManualCpc = (biddingStrategyType?: string | null) => (biddingStrategyType ?? '').startsWith('MANUAL_CPC');

/**
 * A campaign's objective: what the CRM recorded when it built it, else a lead form or
 * conversion bidding means Leads, anything else Website traffic (the same rule the
 * all-campaigns list uses for its objective pill).
 */
export function searchObjectiveOf(
  c: GoogleAdsCampaignSummary, tags: Record<string, SearchObjectiveKey>, leadFormIds: Set<string>,
): SearchObjectiveKey {
  const tagged = tags[c.id];
  if (tagged) return tagged;
  if (leadFormIds.has(c.id)) return 'leads';
  return /CONVERSION|TARGET_CPA|TARGET_ROAS/.test(c.biddingStrategyType ?? '') ? 'leads' : 'traffic';
}

// ── keywords ─────────────────────────────────────────────────────────────

export interface KeywordDraft { id: string; text: string; matchType: SearchMatchType }

export const MATCH_LABEL: Record<SearchMatchType, string> = { BROAD: 'Broad', PHRASE: 'Phrase', EXACT: 'Exact' };
export const MATCH_HINT: Record<SearchMatchType, string> = {
  BROAD: 'Broad — related searches too (most reach)',
  PHRASE: 'Phrase — searches that include the meaning',
  EXACT: 'Exact — the same meaning only (most precise)',
};
const MATCH_TONE: Record<SearchMatchType, string> = {
  BROAD: 'bg-surface-alt text-text-secondary',
  PHRASE: 'bg-info-soft text-info',
  EXACT: 'bg-primary-soft text-primary',
};
const NEXT_MATCH: Record<SearchMatchType, SearchMatchType> = { BROAD: 'PHRASE', PHRASE: 'EXACT', EXACT: 'BROAD' };

/** Characters Google refuses in keyword text — mirrors the backend check. */
const KEYWORD_INVALID = /[!@%,*(){}=;~`<>?\\|^]/;

export function keywordProblem(text: string): string | null {
  const t = text.trim();
  if (!t) return 'empty';
  if (t.length > 80) return 'over 80 characters';
  if (t.split(/\s+/).length > 10) return 'more than 10 words';
  const bad = t.match(KEYWORD_INVALID);
  return bad ? `Google does not allow "${bad[0]}" in keywords` : null;
}

/** One pasted / typed entry → keyword. `"x"` = phrase, `[x]` = exact, bare = the chosen default. */
export function parseKeywordToken(raw: string, fallback: SearchMatchType): KeywordDraft | null {
  let t = raw.trim();
  if (!t) return null;
  let matchType = fallback;
  if (/^\[.*\]$/.test(t)) { matchType = 'EXACT'; t = t.slice(1, -1); }
  else if (/^".*"$/.test(t)) { matchType = 'PHRASE'; t = t.slice(1, -1); }
  else if (/^\+/.test(t)) t = t.replace(/(^|\s)\+/g, '$1');
  t = t.replace(/\s+/g, ' ').trim();
  return t ? { id: uid(), text: t, matchType } : null;
}

/** Add typed/pasted text (newline- or comma-separated) to a list, skipping duplicates (same text + match type). */
export function withKeywords(list: KeywordDraft[], raw: string, fallback: SearchMatchType): KeywordDraft[] {
  const out = [...list];
  const seen = new Set(out.map((k) => `${k.matchType}:${k.text.toLowerCase()}`));
  for (const piece of raw.split(/\n|,(?=(?:[^"]*"[^"]*")*[^"]*$)/)) {
    const k = parseKeywordToken(piece, fallback);
    if (!k) continue;
    const key = `${k.matchType}:${k.text.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(k);
  }
  return out;
}

/** Keyword chips with per-chip match type (click the badge to cycle), paste-a-list, Backspace to remove. */
export function KeywordEditor({ label, hint, keywords, onChange, defaultMatch, onDefaultMatch, negative, disabled, action, placeholder, max = 200 }: {
  label: string; hint?: string; keywords: KeywordDraft[]; onChange: (v: KeywordDraft[]) => void;
  defaultMatch: SearchMatchType; onDefaultMatch?: (m: SearchMatchType) => void;
  negative?: boolean; disabled?: boolean; action?: React.ReactNode; placeholder?: string; max?: number;
}) {
  const [draft, setDraft] = useState('');
  const commit = (raw: string) => {
    if (!raw.trim()) { setDraft(''); return; }
    onChange(withKeywords(keywords, raw, defaultMatch).slice(0, max));
    setDraft('');
  };
  const bad = keywords.filter((k) => keywordProblem(k.text)).length;
  return (
    <div className="rounded-xl border border-border p-3.5">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-text-primary">{label}</div>
          {hint && <p className="mt-0.5 text-2xs text-text-muted">{hint}</p>}
        </div>
        <span className={clsx('pt-0.5 text-2xs tabular-nums', bad ? 'text-danger' : 'text-text-muted')}>
          {keywords.length} keyword{keywords.length === 1 ? '' : 's'}{bad ? ` · ${bad} to fix` : ''}
        </span>
        {action}
      </div>

      {onDefaultMatch && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <span className="text-2xs text-text-muted">New keywords as</span>
          <div className="inline-flex overflow-hidden rounded-lg border border-border">
            {(['BROAD', 'PHRASE', 'EXACT'] as SearchMatchType[]).map((m) => (
              <button key={m} type="button" disabled={disabled} onClick={() => onDefaultMatch(m)} title={MATCH_HINT[m]}
                className={clsx('px-2.5 py-1 text-2xs font-medium transition-colors', defaultMatch === m ? 'bg-primary-soft text-primary' : 'text-text-secondary hover:bg-surface-alt')}>
                {MATCH_LABEL[m]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2.5">
        <Input value={draft} disabled={disabled}
          placeholder={placeholder ?? (negative ? 'e.g. free, jobs, course — press Enter' : 'Type a keyword and press Enter — or paste a list')}
          onChange={(e) => { const v = e.target.value; if (/[\n,]/.test(v)) commit(v); else setDraft(v); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
            else if (e.key === 'Backspace' && !draft && keywords.length) onChange(keywords.slice(0, -1));
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/[\n,]/.test(text)) { e.preventDefault(); commit(`${draft}${text}`); }
          }}
          onBlur={() => { if (draft.trim()) commit(draft); }} />
      </div>

      {keywords.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {keywords.map((k) => {
            const problem = keywordProblem(k.text);
            return (
              <span key={k.id} title={problem ?? MATCH_HINT[k.matchType]}
                className={clsx('inline-flex max-w-full items-center gap-1 rounded-lg border py-0.5 pl-1 pr-1.5 text-xs',
                  problem ? 'border-danger/50 bg-danger-soft text-danger' : 'border-border bg-surface text-text-primary')}>
                <button type="button" disabled={disabled} onClick={() => onChange(keywords.map((x) => (x.id === k.id ? { ...x, matchType: NEXT_MATCH[x.matchType] } : x)))}
                  className={clsx('shrink-0 rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wide', MATCH_TONE[k.matchType])}
                  aria-label={`Match type ${MATCH_LABEL[k.matchType]} — click to change`}>
                  {MATCH_LABEL[k.matchType]}
                </button>
                <span className="truncate">{negative ? '−' : ''}{k.text}</span>
                {!disabled && (
                  <button type="button" onClick={() => onChange(keywords.filter((x) => x.id !== k.id))} aria-label={`Remove ${k.text}`}
                    className="shrink-0 rounded text-text-muted hover:text-danger"><X size={11} /></button>
                )}
              </span>
            );
          })}
        </div>
      )}
      {!negative && keywords.length === 0 && (
        <p className="mt-2 text-2xs text-text-muted">Paste a list — <span className="font-mono">"quotes"</span> add phrase match, <span className="font-mono">[brackets]</span> exact match.</p>
      )}
    </div>
  );
}

// ── responsive search ads ────────────────────────────────────────────────

export type RsaDraft = {
  id: string; name: string; finalUrl: string; headlines: string[]; descriptions: string[]; path1: string; path2: string;
};
export type SearchGroupDraft = {
  id: string; name: string; cpcBid: string; defaultMatch: SearchMatchType;
  keywords: KeywordDraft[]; negatives: KeywordDraft[]; ads: RsaDraft[];
};

export const HEADLINE_MAX = 30;
export const DESCRIPTION_MAX = 90;

export const newRsa = (n: number, finalUrl = ''): RsaDraft => ({
  id: uid(), name: `Ad ${n}`, finalUrl, headlines: ['', '', ''], descriptions: ['', ''], path1: '', path2: '',
});
export const newSearchGroup = (n: number, finalUrl = ''): SearchGroupDraft => ({
  id: uid(), name: `Ad group ${n}`, cpcBid: '1', defaultMatch: 'PHRASE', keywords: [], negatives: [], ads: [newRsa(1, finalUrl)],
});
export const cloneRsa = (a: RsaDraft): RsaDraft => ({ ...a, id: uid(), name: a.name ? `${a.name} (copy)` : '', headlines: [...a.headlines], descriptions: [...a.descriptions] });
export const cloneSearchGroup = (g: SearchGroupDraft): SearchGroupDraft => ({
  ...g, id: uid(), name: g.name ? `${g.name} (copy)` : '',
  keywords: g.keywords.map((k) => ({ ...k, id: uid() })), negatives: g.negatives.map((k) => ({ ...k, id: uid() })), ads: g.ads.map(cloneRsa),
});

/** AI draft group → editable draft (fresh ids, headline/description lists padded to Google's minimums). */
export function groupFromAi(g: SearchAiDraftGroup, n: number): SearchGroupDraft {
  const pad = (xs: string[], min: number) => (xs.length >= min ? [...xs] : [...xs, ...Array(min - xs.length).fill('')]);
  return {
    id: uid(), name: g.name || `Ad group ${n}`, cpcBid: '1', defaultMatch: 'PHRASE',
    keywords: g.keywords.map((k) => ({ id: uid(), text: k.text, matchType: k.matchType })),
    negatives: g.negatives.map((t) => ({ id: uid(), text: t, matchType: 'PHRASE' as SearchMatchType })),
    ads: [{
      id: uid(), name: 'Ad 1', finalUrl: g.ad.finalUrl, headlines: pad(g.ad.headlines, 3), descriptions: pad(g.ad.descriptions, 2),
      path1: g.ad.path1, path2: g.ad.path2,
    }],
  };
}

const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);
const pathOk = (p: string) => p.length <= 15 && !/[/?#\s]/.test(p);

export const rsaIssues = (a: RsaDraft): string[] => {
  const i: string[] = [];
  if (!isHttpUrl(a.finalUrl)) i.push('landing page URL (https://…)');
  const hs = filled(a.headlines);
  const ds = filled(a.descriptions);
  if (hs.length < 3) i.push(`${3 - hs.length} more headline${3 - hs.length === 1 ? '' : 's'}`);
  if (ds.length < 2) i.push(`${2 - ds.length} more description${2 - ds.length === 1 ? '' : 's'}`);
  if (a.headlines.some((h) => h.length > HEADLINE_MAX)) i.push(`headlines ≤ ${HEADLINE_MAX} characters`);
  if (a.descriptions.some((d) => d.length > DESCRIPTION_MAX)) i.push(`descriptions ≤ ${DESCRIPTION_MAX} characters`);
  if (new Set(hs.map((h) => h.toLowerCase())).size !== hs.length) i.push('different headlines (two are the same)');
  if (new Set(ds.map((d) => d.toLowerCase())).size !== ds.length) i.push('different descriptions (two are the same)');
  if (hs.some((h) => h.includes('!'))) i.push('headlines without "!" (Google refuses them)');
  if (!pathOk(a.path1) || !pathOk(a.path2)) i.push('display paths ≤ 15 characters, no spaces or slashes');
  if (a.path2 && !a.path1) i.push('path 1 before path 2');
  return i;
};

export const searchGroupIssues = (g: SearchGroupDraft, manualCpc: boolean, withAds = true): string[] => {
  const i: string[] = [];
  if (!g.name.trim()) i.push('a name');
  if (manualCpc && toMicros(g.cpcBid) <= 0) i.push('a max CPC above zero');
  if (g.keywords.length === 0) i.push('at least one keyword');
  const bad = [...g.keywords, ...g.negatives].filter((k) => keywordProblem(k.text));
  if (bad.length) i.push(`${bad.length} keyword${bad.length === 1 ? '' : 's'} fixed (${bad.slice(0, 2).map((k) => `"${k.text}"`).join(', ')})`);
  if (withAds && g.ads.length === 0) i.push('at least one ad');
  return i;
};

export const groupSummary = (g: SearchGroupDraft) =>
  `${g.keywords.length} keyword${g.keywords.length === 1 ? '' : 's'}${g.negatives.length ? ` · ${g.negatives.length} negative` : ''} · ${g.ads.length} ad${g.ads.length === 1 ? '' : 's'}`;

// ── ad strength ──────────────────────────────────────────────────────────

export interface AdStrength { level: 0 | 1 | 2 | 3 | 4; label: 'Incomplete' | 'Poor' | 'Average' | 'Good' | 'Excellent'; tips: string[] }

/**
 * An estimate of Google's Ad strength: enough distinct headlines and
 * descriptions, keywords used in headlines, and headlines that don't all
 * start the same way. Google's own score arrives after the ad is reviewed.
 */
export function adStrength(ad: RsaDraft | null, keywords: KeywordDraft[]): AdStrength {
  if (!ad) return { level: 0, label: 'Incomplete', tips: [] };
  const hs = Array.from(new Set(filled(ad.headlines).map((h) => h.toLowerCase())));
  const ds = Array.from(new Set(filled(ad.descriptions).map((d) => d.toLowerCase())));
  const tips: string[] = [];
  if (hs.length < 3 || ds.length < 2) {
    return { level: 0, label: 'Incomplete', tips: [`Google needs at least 3 headlines and 2 descriptions (${hs.length}/3 · ${ds.length}/2).`] };
  }
  let score = (Math.min(hs.length, 15) / 15) * 40 + (Math.min(ds.length, 4) / 4) * 20;
  if (hs.length < 10) tips.push(`Add ${10 - hs.length} more headline${10 - hs.length === 1 ? '' : 's'} — 10+ lets Google find the winning combinations.`);
  if (ds.length < 4) tips.push(`Add ${4 - ds.length} more description${4 - ds.length === 1 ? '' : 's'}.`);
  const words = Array.from(new Set(keywords.flatMap((k) => k.text.toLowerCase().split(/\s+/)).filter((w) => w.length > 3)));
  if (words.length) {
    const withKw = hs.filter((h) => words.some((w) => h.includes(w))).length;
    score += (Math.min(withKw, 3) / 3) * 25;
    if (withKw < 2) tips.push('Use your keywords in at least two headlines.');
  } else {
    score += 10;
    tips.push('Add keywords to the ad group so the headlines can match them.');
  }
  const starts = new Set(hs.map((h) => h.split(/\s+/).slice(0, 2).join(' ')));
  score += (starts.size / hs.length) * 15;
  if (starts.size / hs.length < 0.7) tips.push('Vary the headlines — too many start the same way.');
  const level = score < 45 ? 1 : score < 65 ? 2 : score < 85 ? 3 : 4;
  return { level, label: (['Incomplete', 'Poor', 'Average', 'Good', 'Excellent'] as const)[level], tips: tips.slice(0, 3) };
}

const STRENGTH_TONE: Record<AdStrength['level'], string> = {
  0: 'bg-surface-alt text-text-muted', 1: 'bg-danger-soft text-danger', 2: 'bg-warning-soft text-warning', 3: 'bg-info-soft text-info', 4: 'bg-success-soft text-success',
};
const STRENGTH_BAR: Record<AdStrength['level'], string> = { 0: 'bg-border-strong', 1: 'bg-danger', 2: 'bg-warning', 3: 'bg-info', 4: 'bg-success' };

export const StrengthPill = ({ s, className }: { s: AdStrength; className?: string }) => (
  <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold', STRENGTH_TONE[s.level], className)}>
    {s.label}
  </span>
);

export function StrengthMeter({ s }: { s: AdStrength }) {
  return (
    <div>
      <div className="flex items-center justify-between text-2xs">
        <span className="font-semibold uppercase tracking-wide text-text-muted">Ad strength</span>
        <StrengthPill s={s} />
      </div>
      <div className="mt-1.5 grid grid-cols-4 gap-1">
        {[1, 2, 3, 4].map((i) => <span key={i} className={clsx('h-1.5 rounded-full', i <= s.level ? STRENGTH_BAR[s.level] : 'bg-surface-alt')} />)}
      </div>
      {s.tips.length > 0 && (
        <ul className="mt-2 space-y-1">
          {s.tips.map((t) => <li key={t} className="flex gap-1.5 text-2xs leading-4 text-text-muted"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-text-muted" />{t}</li>)}
        </ul>
      )}
    </div>
  );
}

// ── editors ──────────────────────────────────────────────────────────────

/** Headlines / descriptions: one input per line with a live counter against Google's limit. */
function CopyLines({ label, items, onChange, min, max, maxLen, placeholder, disabled, cols = 1, hint, banned }: {
  label: string; items: string[]; onChange: (v: string[]) => void; min: number; max: number; maxLen: number;
  placeholder: string[]; disabled?: boolean; cols?: 1 | 2; hint?: string;
  /** Characters Google refuses on this line type — the line turns red with the reason. */
  banned?: { re: RegExp; message: string };
}) {
  const count = filled(items).length;
  const bannedHit = !!banned && items.some((v) => banned.re.test(v));
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <Label>{label}</Label>
        <span className={clsx('text-2xs tabular-nums', count < min ? 'text-warning' : 'text-text-muted')}>{count}/{max}{count < min ? ` · need ${min}` : ''}</span>
      </div>
      {hint && <p className="mt-0.5 text-2xs text-text-muted">{hint}</p>}
      {/* Two columns only on very wide screens — beside the step rail and preview a 30-char headline gets cut off. */}
      <div className={clsx('mt-1.5 grid gap-1.5', cols === 2 ? 'grid-cols-1 2xl:grid-cols-2' : 'grid-cols-1')}>
        {items.map((v, i) => {
          const over = v.length > maxLen;
          const bad = over || (!!banned && banned.re.test(v));
          return (
            <div key={i} className="flex items-center gap-1">
              <div className="relative min-w-0 flex-1">
                <Input value={v} disabled={disabled} maxLength={maxLen + 20} placeholder={placeholder[i] ?? ''} aria-label={`${label} ${i + 1}`}
                  onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} className="pr-12" invalid={bad} />
                <span className={clsx('pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-2xs tabular-nums', bad ? 'text-danger' : v.length > maxLen - 4 ? 'text-warning' : 'text-text-muted/70')}>
                  {v.length}/{maxLen}
                </span>
              </div>
              {items.length > min && !disabled && (
                <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}
                  className="flex h-8 w-7 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"><X size={13} /></button>
              )}
            </div>
          );
        })}
      </div>
      {bannedHit && <p className="mt-1 text-2xs text-danger">{banned!.message}</p>}
      {items.length < max && !disabled && (
        <button type="button" onClick={() => onChange([...items, ''])} className="mt-1.5 inline-flex items-center gap-1 text-2xs font-medium text-primary hover:underline">
          <Plus size={11} />Add {label.toLowerCase().replace(/s$/, '')}
        </button>
      )}
    </div>
  );
}

export function RsaEditor({ ad, onChange, disabled, onWrite, writing, aiFilled }: {
  ad: RsaDraft; onChange: (p: Partial<RsaDraft>) => void; disabled?: boolean;
  /** "Write with AI" for this ad; omitted when AI is unavailable. */
  onWrite?: () => void; writing?: boolean; aiFilled?: boolean;
}) {
  const host = isHttpUrl(ad.finalUrl) ? hostOf(ad.finalUrl) : 'yourwebsite.com';
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_200px]">
        <div>
          <Label>Landing page URL</Label>
          <Input className="mt-1" iconLeft={<Globe size={13} />} placeholder="https://yourwebsite.com/page" value={ad.finalUrl} disabled={disabled}
            onChange={(e) => onChange({ finalUrl: e.target.value.trim() })} />
        </div>
        <div>
          <Label>Ad name <span className="font-normal text-text-muted">(internal)</span></Label>
          <Input className="mt-1" placeholder="e.g. Offer ad v1" value={ad.name} disabled={disabled} onChange={(e) => onChange({ name: e.target.value })} />
        </div>
      </div>

      <div>
        <Label>Display URL</Label>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
          <span className="max-w-[40%] truncate rounded-lg bg-surface-alt px-2.5 py-2 font-medium text-text-secondary">{host}</span>
          <span>/</span>
          <Input className="!w-36" maxLength={15} placeholder="path-1" value={ad.path1} disabled={disabled} aria-label="Display path 1"
            onChange={(e) => onChange({ path1: e.target.value.replace(/[/?#\s]/g, '') })} />
          <span>/</span>
          <Input className="!w-36" maxLength={15} placeholder="path-2" value={ad.path2} disabled={disabled} aria-label="Display path 2"
            onChange={(e) => onChange({ path2: e.target.value.replace(/[/?#\s]/g, '') })} />
        </div>
        <p className="mt-1 text-2xs text-text-muted">Optional words after your domain — they show in the ad, not the real link (≤ 15 characters each).</p>
      </div>

      <div className="rounded-xl border border-border p-3.5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-text-primary">
              Ad copy
              {aiFilled && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary-soft px-1.5 py-0.5 align-middle text-[10px] font-medium text-primary"><Sparkles size={9} /> AI suggested</span>}
            </div>
            <p className="text-2xs text-text-muted">Google mixes these into the best combination for each search.</p>
          </div>
          {onWrite && (
            <Button size="sm" variant="soft" iconLeft={<Wand2 size={13} />} loading={writing} disabled={disabled || writing} onClick={onWrite}>
              {filled(ad.headlines).length >= 3 ? 'Rewrite with AI' : 'Write with AI'}
            </Button>
          )}
        </div>
        <div className="space-y-4">
          <CopyLines label="Headlines" items={ad.headlines} onChange={(v) => onChange({ headlines: v })} min={3} max={15} maxLen={HEADLINE_MAX} disabled={disabled} cols={2}
            hint="Up to 15, each ≤ 30 characters. No exclamation marks."
            banned={{ re: /!/, message: 'Remove the “!” — Google refuses headlines with exclamation marks.' }}
            placeholder={['Book Your Appointment', 'Same-Day Availability', 'Trusted by 5,000 Patients', 'Open 7 Days a Week', 'Free First Consultation']} />
          <CopyLines label="Descriptions" items={ad.descriptions} onChange={(v) => onChange({ descriptions: v })} min={2} max={4} maxLen={DESCRIPTION_MAX} disabled={disabled}
            placeholder={['Modern clinic in Dubai Marina. Call or book online in 30 seconds.', 'Gentle care, transparent prices and weekend hours for the whole family.']} />
        </div>
      </div>
    </div>
  );
}

export function SearchGroupEditor({ group, onChange, manualCpc, disabled, currency, onSuggest, suggesting }: {
  group: SearchGroupDraft; onChange: (p: Partial<SearchGroupDraft>) => void; manualCpc: boolean; disabled?: boolean; currency?: string | null;
  onSuggest?: () => void; suggesting?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className={manualCpc ? 'grid grid-cols-1 gap-3 sm:grid-cols-[1fr_200px]' : ''}>
        <div>
          <Label>Ad group name</Label>
          <Input className="mt-1" placeholder="e.g. Teeth whitening · Dubai" value={group.name} disabled={disabled} onChange={(e) => onChange({ name: e.target.value })} />
        </div>
        {manualCpc && (
          <div>
            <Label>Max CPC</Label>
            <div className="mt-1"><MoneyInput value={group.cpcBid} onChange={(v) => onChange({ cpcBid: v })} currency={currency} disabled={disabled} /></div>
          </div>
        )}
      </div>
      <KeywordEditor label="Keywords" hint="The searches that should show these ads. Group one theme per ad group."
        keywords={group.keywords} onChange={(v) => onChange({ keywords: v })} defaultMatch={group.defaultMatch}
        onDefaultMatch={(m) => onChange({ defaultMatch: m })} disabled={disabled}
        action={onSuggest && (
          <Button size="sm" variant="soft" iconLeft={<Wand2 size={13} />} loading={suggesting} disabled={disabled || suggesting} onClick={onSuggest}>
            Suggest with AI
          </Button>
        )} />
      <KeywordEditor label="Negative keywords" hint="Searches that must NOT show the ads — e.g. free, jobs, course. Optional."
        keywords={group.negatives} onChange={(v) => onChange({ negatives: v })} defaultMatch="PHRASE" negative disabled={disabled} max={200} />
    </div>
  );
}

// ── preview ──────────────────────────────────────────────────────────────

const rotate = <T,>(xs: T[], by: number) => (xs.length ? [...xs.slice(by % xs.length), ...xs.slice(0, by % xs.length)] : xs);

/** How the ad reads on a Google results page — one likely combination, reshuffled on demand. */
export function SearchAdPreview({ ad, keywords, businessName }: { ad: RsaDraft | null; keywords: KeywordDraft[]; businessName?: string | null }) {
  const [device, setDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [shift, setShift] = useState(0);
  const hs = useMemo(() => filled(ad?.headlines ?? []), [ad]);
  const ds = useMemo(() => filled(ad?.descriptions ?? []), [ad]);
  const strength = useMemo(() => adStrength(ad, keywords), [ad, keywords]);
  const host = ad?.finalUrl && isHttpUrl(ad.finalUrl) ? hostOf(ad.finalUrl) : 'yourwebsite.com';
  const path = [ad?.path1, ad?.path2].filter(Boolean).join(' › ');
  const headline = rotate(hs, shift).slice(0, device === 'mobile' ? 2 : 3).join(' | ');
  const description = rotate(ds, shift).slice(0, 2).join(' ');
  const sample = keywords.slice(0, 6);

  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className="min-w-0 flex-1 text-xs font-semibold text-text-muted">Preview · how it reads on Google</span>
        <div className="inline-flex overflow-hidden rounded-lg border border-border">
          <button type="button" onClick={() => setDevice('mobile')} aria-pressed={device === 'mobile'} title="Mobile" aria-label="Mobile preview"
            className={clsx('px-1.5 py-1', device === 'mobile' ? 'bg-surface-alt text-text-primary' : 'text-text-muted')}><Smartphone size={13} /></button>
          <button type="button" onClick={() => setDevice('desktop')} aria-pressed={device === 'desktop'} title="Desktop" aria-label="Desktop preview"
            className={clsx('px-1.5 py-1', device === 'desktop' ? 'bg-surface-alt text-text-primary' : 'text-text-muted')}><Monitor size={13} /></button>
        </div>
        <button type="button" onClick={() => setShift((n) => n + 1)} disabled={hs.length < 2} title="Show another combination" aria-label="Show another combination"
          className="flex h-6 w-6 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary disabled:opacity-40"><Shuffle size={13} /></button>
      </div>
      <div className="p-4">
        {!ad ? (
          <p className="py-10 text-center text-xs text-text-muted">Add an ad to see it previewed here.</p>
        ) : (
          <div className={clsx('mx-auto rounded-xl border border-border bg-white p-4 text-[#202124] shadow-soft dark:bg-[#1f1f1f] dark:text-[#e8eaed]', device === 'mobile' ? 'max-w-[290px]' : 'w-full')}>
            <div className="text-[11px] font-bold">Sponsored</div>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#e8f0fe] text-[11px] font-bold uppercase text-[#1a73e8]">{host.charAt(0)}</span>
              <div className="min-w-0 leading-tight">
                <div className="truncate text-xs font-medium">{businessName || host}</div>
                <div className="truncate text-[11px] text-[#5f6368] dark:text-[#9aa0a6]">https://{host}{path ? ` › ${path}` : ''}</div>
              </div>
            </div>
            <div className={clsx('mt-1.5 leading-snug text-[#1a0dab] dark:text-[#8ab4f8]', device === 'mobile' ? 'text-[16px]' : 'text-[18px]')}>
              {headline || <span className="text-[#5f6368]">Your headlines appear here</span>}
            </div>
            <div className="mt-1 text-[13px] leading-snug text-[#4d5156] dark:text-[#bdc1c6]">
              {description || <span className="text-[#9aa0a6]">Your descriptions appear here.</span>}
            </div>
          </div>
        )}
        <div className="mt-4 border-t border-border pt-3">
          <StrengthMeter s={strength} />
        </div>
        {sample.length > 0 && (
          <div className="mt-3 border-t border-border pt-3">
            <div className="text-2xs font-semibold uppercase tracking-wide text-text-muted">Shows for searches like</div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {sample.map((k) => <span key={k.id} className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-secondary">{k.text}</span>)}
              {keywords.length > sample.length && <span className="px-1 text-2xs text-text-muted">+{keywords.length - sample.length} more</span>}
            </div>
          </div>
        )}
        <p className="mt-3 text-[10px] leading-relaxed text-text-muted">
          Indicative only — Google assembles headlines and descriptions per search and device.
        </p>
      </div>
    </div>
  );
}

/** "AI suggested" marker; disappears once the user edits the field. */
export const AiBadge = ({ on }: { on: boolean }) => on ? (
  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary-soft px-1.5 py-0.5 align-middle text-[10px] font-medium text-primary">
    <Sparkles size={9} /> AI suggested
  </span>
) : null;

/** One labelled value on the review screen. */
export const Fact = ({ label, value, sub, wide }: { label: string; value: React.ReactNode; sub?: string; wide?: boolean }) => (
  <div className={wide ? 'sm:col-span-2' : undefined}>
    <div className="text-2xs font-semibold uppercase tracking-wide text-text-muted">{label}</div>
    <div className="mt-0.5 break-words text-[13px] font-medium text-text-primary">{value}</div>
    {sub && <div className="text-2xs text-text-muted">{sub}</div>}
  </div>
);

/** Pill list for the review (locations, languages, keywords). */
export const ChipList = ({ items, empty, max = 14 }: { items: string[]; empty: string; max?: number }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.length === 0 && <span className="rounded-full bg-surface-alt px-2.5 py-1 text-xs text-text-secondary">{empty}</span>}
    {items.slice(0, max).map((x) => <span key={x} title={x} className="max-w-full truncate rounded-full border border-border bg-surface px-2.5 py-1 text-xs text-text-primary">{x}</span>)}
    {items.length > max && <span className="rounded-full bg-surface-alt px-2.5 py-1 text-xs text-text-muted">+{items.length - max} more</span>}
  </div>
);

export const CheckMark = () => <Check size={12} className="text-success" />;
